import React from 'react'
import { Card, CardContent, CardHeader, Label, Switch, Select } from '@/shared/ui'
import { getChartTypeSettings, setChartActiveType, setChartEnabledForMaster, setChartEnabledForUsers, type ChartActiveType } from '@/modules/graph-view/services/chartTypeService'
import { useToast } from '@/shared/ui/Toast'

export default function ChartTypeSettings() {
  const { add: toast } = useToast()
  const [loading, setLoading] = React.useState(true)
  const [saving, setSaving] = React.useState(false)
  const [type, setType] = React.useState<ChartActiveType>('default')
  const [masterOn, setMasterOn] = React.useState(false)
  const [usersOn, setUsersOn] = React.useState(false)

  React.useEffect(() => {
    let mounted = true
    getChartTypeSettings()
      .then((cfg) => { if (!mounted) return; setType(cfg.activeType); setMasterOn(cfg.enabledForMaster); setUsersOn(cfg.enabledForUsers) })
      .catch((e) => toast({ title: 'Gráfico', description: e?.message || 'Falha ao carregar configuração do gráfico', variant: 'error' }))
      .finally(() => { if (mounted) setLoading(false) })
    return () => { mounted = false }
  }, [])

  async function handleTypeChange(next: ChartActiveType) {
    if (saving) return
    setSaving(true)
    try {
      await setChartActiveType(next)
      setType(next)
      toast({ title: 'Gráfico', description: next === 'tv_v2' ? 'TradingView v2 selecionado' : 'Gráfico padrão selecionado', variant: 'success', soft: true })
    } catch (e: any) {
      toast({ title: 'Gráfico', description: e?.message || 'Falha ao salvar tipo do gráfico', variant: 'error' })
    } finally { setSaving(false) }
  }

  async function handleToggleMaster(next: boolean) {
    if (saving) return
    setSaving(true)
    try {
      await setChartEnabledForMaster(next)
      setMasterOn(next)
      toast({ title: 'Gráfico (Master)', description: next ? 'Ativado para master' : 'Desativado para master', variant: 'success', soft: true })
    } catch (e: any) {
      toast({ title: 'Gráfico (Master)', description: e?.message || 'Falha ao salvar', variant: 'error' })
    } finally { setSaving(false) }
  }

  async function handleToggleUsers(next: boolean) {
    if (saving) return
    setSaving(true)
    try {
      await setChartEnabledForUsers(next)
      setUsersOn(next)
      toast({ title: 'Gráfico (Usuários)', description: next ? 'Ativado para usuários' : 'Desativado para usuários', variant: 'success', soft: true })
    } catch (e: any) {
      toast({ title: 'Gráfico (Usuários)', description: e?.message || 'Falha ao salvar', variant: 'error' })
    } finally { setSaving(false) }
  }

  return (
    <Card>
      <CardHeader className="text-base font-semibold">Gráfico: Tipo e Controle de Acesso</CardHeader>
      <CardContent>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label>Tipo de Gráfico</Label>
            <Select value={type} onChange={(e)=> handleTypeChange(e.target.value as ChartActiveType)} disabled={loading || saving}>
              <option value="default">Padrão (Lightweight)</option>
              <option value="tv_v2">TradingView v2</option>
            </Select>
            <div className="text-xs text-foreground/70">Quando ativo, o TradingView v2 substitui o gráfico padrão mantendo as mesmas proporções (desktop e mobile).</div>
          </div>
          <div className="space-y-2">
            <Label>Ativação por Perfil</Label>
            <div className="flex items-center justify-between rounded-md border border-border p-3">
              <div className="text-sm">Ativo para Master</div>
              <div className="flex items-center gap-2">
                <Label>Desativado</Label>
                <Switch checked={masterOn} onChange={(e)=> handleToggleMaster((e.target as HTMLInputElement).checked)} disabled={loading || saving} />
                <Label>Ativado</Label>
              </div>
            </div>
            <div className="flex items-center justify-between rounded-md border border-border p-3">
              <div className="text-sm">Ativo para Usuários (não-master)</div>
              <div className="flex items-center gap-2">
                <Label>Desativado</Label>
                <Switch checked={usersOn} onChange={(e)=> handleToggleUsers((e.target as HTMLInputElement).checked)} disabled={loading || saving} />
                <Label>Ativado</Label>
              </div>
            </div>
            <div className="text-xs text-foreground/70">Se desativado para um perfil, o gráfico padrão é usado como fallback.</div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
